import { uniq } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Alert,
    AlertHandle,
    autoinject,
    bind,
    Enums,
    FormStore,
    inject,
    LoadingOverlay,
    LoadingOverlayHandle,
    Modal,
    ModalHandle,
    TasksStore,
    TicketForm,
    TicketsStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsExclamationTriangleFill } from '@react-icons/all-files/bs/BsExclamationTriangleFill';
import { BsPlusSquare } from '@react-icons/all-files/bs/BsPlusSquare';
import { Button } from 'reactstrap';
import { ContractsStore } from '../Contracts/ContractsStore';
import { ZendeskLink } from '../Core/ZendeskLink';
import { ActiveDisplay } from '../Displays/ActiveDisplay';

export interface ITicketModalProps {
    readonly serialNumber?: string;
    readonly modalHandle: ModalHandle;
    readonly displayEvents?: Mri.DisplayEvents.DisplayEvent[];
    readonly display: ActiveDisplay;
}

@autoinject
@observer
export class TicketModal extends React.Component<ITicketModalProps> {
    public render() {
        return (
            <>
                <Modal
                    title={this.title}
                    modalHandle={this.props.modalHandle}
                    bodyClass="p-0 rounded"
                    onOpened={this.loadAsync}
                    footerClass="d-none"
                >
                    <span className="text-center">
                        <Alert alertHandle={this.alertHandle} />
                    </span>
                    <div className="ticket-modal">
                        <LoadingOverlay loadingOverlayHandle={this.loadingOverlayHandle} className="h-100" />

                        {!this.inWarranty && (
                            <div className="text-center">
                                <h5>
                                    <BsExclamationTriangleFill className="text-warning p-2" />
                                    Your SmartVu® coverage has expired
                                </h5>

                                <div>
                                    If you still need support, MRI would be more than happy to service you. Since your
                                    coverage has expired this will be considered Time & Material. Please let us know if
                                    you would like a Time & Material Quote.
                                </div>

                                <a href="https://mri-inc.net/find-a-reseller/" className="btn btn-link" target="_blank">
                                    Contact MRI Support
                                </a>
                            </div>
                        )}

                        {this.inWarranty && this.ticketForm && (
                            <TicketForm
                                onFormChange={this.populateTicket}
                                onSubmit={this.submitAsync}
                                ticketForm={this.ticketForm}
                                alertHandle={this.alertHandle}
                                isPortal
                            />
                        )}
                    </div>
                </Modal>
                <Modal
                    modalHandle={this.modalHandle}
                    title={`Created #${this.createdTicketedId}`}
                    footer={
                        <div>
                            <Button onClick={this.modalHandle.close}>Go Back</Button>
                            <ZendeskLink id={this.createdTicketedId!} className="btn btn-primary">
                                View Ticket
                            </ZendeskLink>
                        </div>
                    }
                >
                    Succesfully Created Ticket
                </Modal>
            </>
        );
    }

    //Private Functions
    @bind
    @action
    private async submitAsync(fields: any) {
        const task = this.tasksStore.startSessionTask(`Submit Ticket`);

        try {
            const zendeskTicketModel = await this.ticketsStore.createAsync(fields);

            if (zendeskTicketModel) {
                this.createdTicketedId = zendeskTicketModel.id;
                this.modalHandle.open();
                this.props.modalHandle.close();
                this.ticketForm = undefined;
            }

            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    @action
    private async loadAsync() {
        if (this.ticketForm) {
            this.ticketForm = undefined;
        }
        this.loadingOverlayHandle.set('Loading Ticket', 'fa-ticket-alt');

        let defaultFormId = null;
        this.autoGeneratedSummary = null;

        try {
            await this.ticketsStore.loadCreateMetaAsync();

            if (!this.ticketsStore.createMeta) throw new Error('Create Meta Undefined');

            const defaultForm = this.ticketsStore.createMeta!.forms.find(f => f.default);

            if (defaultForm) defaultFormId = defaultForm.id;

            this.ticketForm = new FormStore<any>({
                ticket_form_id: defaultFormId,
                internal_note: null,
                114097823112: null,
                tags: null,
                114097823092: null,
                114102019472: null,
            });

            this.populateTicket();
        } catch (e: any) {
            this.alertHandle.setError('There was an error while loading the Ticket Form');
        } finally {
            this.loadingOverlayHandle.close();
        }
    }

    @bind
    @action
    private populateTicket() {
        if (!this.ticketForm) throw new Error('Ticket Form Not Defined');

        if (this.ticketForm.fields[114102019472])
            this.ticketForm.fields[114102019472].onChange(this.props.display.serialNumber);

        this.ticketForm.add(360000185832 as any, null);

        if (this.props.displayEvents) {
            let types = this.props.displayEvents
                .map(de => de.downtimeIncidentType)
                .filter(t => !!t) as Mri.DisplayApi.DowntimeIncidentTypes[];
            types = uniq(types);

            this.ticketForm.fields[360000185832].onChange(
                types.map(v => Enums.toString(Enums.DowntimeIncidentType, v)).join(' ')
            );

            if (this.ticketForm.fields[114097823092].value == this.autoGeneratedSummary) {
                this.autoGeneratedSummary =
                    this.props.display.serialNumber + ': ' + this.props.displayEvents.map(de => de.details).join(', ');
                this.ticketForm.fields[114097823092].onChange(this.autoGeneratedSummary);
            }
        }
    }

    @computed
    private get title() {
        return (
            <span className="text-header fw-bold">
                <BsPlusSquare /> Create Ticket For: {this.props.display.serialNumber}
            </span>
        );
    }

    @computed
    private get contract() {
        if (this.props.display.contract) return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @computed
    private get inWarranty() {
        return this.contract?.inWarranty(this.props.display.shippedDate);
    }

    @observable
    private autoGeneratedSummary: string | null = null;
    @observable
    private loadingOverlayHandle = new LoadingOverlayHandle();
    @observable
    private alertHandle = new AlertHandle();
    @inject
    private tasksStore!: TasksStore;
    @observable
    private ticketForm?: FormStore<any>;
    @observable
    private createdTicketedId?: string;
    private modalHandle = new ModalHandle();
    @inject
    private ticketsStore!: TicketsStore;
    @inject
    private contractsStore!: ContractsStore;
}
